import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/vue";

import { useChatbotService } from "@/services/chatbot-service";
import {
  ChatbotEntity,
  ChatbotMessage,
} from "@/services/chatbot-service/models";
import { resolveMentions } from "@/helpers/mention";
import { useStorageService } from "@/services/storage-service";
import { useAuthStore } from "@/store/auth";

interface PersistedChatbotState {
  threadId?: string;
  messages: ChatbotMessage[];
}

/** Reads the first GraphQL error `extensions.code` from an Apollo error, if any. */
function extractErrorCode(error: unknown): string | undefined {
  const graphQLErrors = (
    error as { graphQLErrors?: Array<{ extensions?: { code?: unknown } }> }
  )?.graphQLErrors;
  const code = graphQLErrors?.[0]?.extensions?.code;
  return typeof code === "string" ? code : undefined;
}

// Setup store (Composition API syntax) rather than an options store: it's a
// feature store, so it's allowed to depend on the core auth store to reset
// itself on logout. The watch below is registered lazily, the first time
// useChatbotStore() is called, at which point Pinia is guaranteed to be
// active — unlike a module-level `useAuthStore()` call, which could run
// before `app.use(pinia)` depending on import order.
export const useChatbotStore = defineStore("chatbot", () => {
  const chatbotService = useChatbotService();
  const storageService = useStorageService();
  const authStore = useAuthStore();

  const persisted = storageService.getChatbotState<PersistedChatbotState>();

  const open = ref(false);
  const sending = ref(false);
  const threadId = ref<string | undefined>(persisted?.threadId);
  const messages = ref<ChatbotMessage[]>(persisted?.messages ?? []);
  const entities = ref<ChatbotEntity[]>([]);
  const entitiesLoaded = ref(false);

  function persist() {
    storageService.setChatbotState({
      threadId: threadId.value,
      messages: messages.value,
    } as PersistedChatbotState);
  }

  function togglePanel() {
    open.value = !open.value;
    if (open.value) {
      loadEntities();
    }
  }

  function closePanel() {
    open.value = false;
  }

  async function loadEntities() {
    if (entitiesLoaded.value) {
      return;
    }

    try {
      entities.value = await chatbotService.fetchChatbotEntities();
      entitiesLoaded.value = true;
    } catch (e) {
      console.error("Failed to load chatbot entities", e);
    }
  }

  async function ensureThread() {
    if (threadId.value) {
      return threadId.value;
    }

    threadId.value = await chatbotService.resetChatbotThread();
    persist();
    return threadId.value;
  }

  async function sendMessage(rawInput: string) {
    if (!rawInput.trim() || sending.value) {
      return;
    }

    sending.value = true;

    const message = resolveMentions(rawInput, entities.value);

    messages.value.push({ id: uuidv4(), role: "user", content: message });
    persist();

    try {
      const usedThreadId = await ensureThread();
      const { response, runId, assets, errorCode } =
        await chatbotService.sendChatMessage(message, usedThreadId);

      if (errorCode) {
        // The API rejected the turn (budget / rate-limit) and returned the code
        // as data. Render a localized error bubble instead of an empty reply.
        messages.value.push({
          id: uuidv4(),
          role: "bot",
          content: "",
          isError: true,
          errorCode,
        });
      } else {
        messages.value.push({
          id: uuidv4(),
          role: "bot",
          content: response,
          runId,
          assets,
        });
      }
    } catch (e) {
      console.error("Chatbot send message failed", e);
      // The error is swallowed here to render a chat bubble instead, so it
      // never reaches Sentry's global handlers — report it explicitly.
      Sentry.captureException(e, { tags: { feature: "chatbot" } });
      messages.value.push({
        id: uuidv4(),
        role: "bot",
        content: "",
        isError: true,
        // Surface known API error codes (budget_exceeded / rate_limited) so the
        // UI shows a specific localized message instead of the generic one.
        errorCode: extractErrorCode(e),
      });
    } finally {
      sending.value = false;
      persist();
    }
  }

  async function submitFeedback(
    messageId: string,
    score: number | null,
    comment?: string,
  ) {
    const message = messages.value.find((m) => m.id === messageId);
    if (!message || !message.runId) {
      return;
    }

    try {
      await chatbotService.submitChatbotFeedback(
        message.runId,
        score ?? undefined,
        comment,
      );

      if (score !== null) {
        message.feedbackScore = score === 1 ? "up" : "down";
      }
      if (comment) {
        message.feedbackComment = comment;
      }
      persist();
    } catch (e) {
      console.error("Chatbot feedback submission failed", e);
    }
  }

  async function resetConversation() {
    messages.value = [];
    threadId.value = undefined;
    persist();
    await ensureThread();
  }

  function clear() {
    messages.value = [];
    threadId.value = undefined;
    open.value = false;
    entities.value = [];
    entitiesLoaded.value = false;
    storageService.removeChatbotState();
  }

  watch(
    () => authStore.user,
    (user) => {
      if (user == null) {
        clear();
      }
    },
  );

  return {
    open,
    sending,
    threadId,
    messages,
    entities,
    togglePanel,
    closePanel,
    loadEntities,
    sendMessage,
    submitFeedback,
    resetConversation,
    clear,
  };
});
